<script setup>
// Addendum §1 ContextMenu — the DropdownMenu behaviour, opened by right-click
// and positioned at the pointer. Same roles, keyboard nav, Esc and outside
// close. Destructive items after a separator, never first (§2.4).
import { ref, nextTick, onBeforeUnmount } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
  items: { type: Array, default: () => [] },
})

const open = ref(false)
const pos = ref({ x: 0, y: 0 })
const menuEl = ref(null)

function menuItems() {
  return menuEl.value ? [...menuEl.value.querySelectorAll('[role="menuitem"]:not([disabled])')] : []
}
async function onContext(e) {
  e.preventDefault()
  pos.value = { x: e.clientX, y: e.clientY }
  open.value = true
  document.addEventListener('pointerdown', onDocPointer, true)
  await nextTick()
  menuItems()[0]?.focus()
}
function close() {
  open.value = false
  document.removeEventListener('pointerdown', onDocPointer, true)
}
function onDocPointer(e) {
  if (menuEl.value && !menuEl.value.contains(e.target)) close()
}
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer, true))

function select(it) {
  if (it.disabled) return
  it.onSelect?.()
  close()
}
function onKey(e) {
  const els = menuItems()
  const cur = els.indexOf(document.activeElement)
  if (e.key === 'ArrowDown') { e.preventDefault(); els[(cur + 1) % els.length]?.focus() }
  else if (e.key === 'ArrowUp') { e.preventDefault(); els[(cur - 1 + els.length) % els.length]?.focus() }
  else if (e.key === 'Escape') { e.preventDefault(); close() }
}
</script>

<template>
  <div class="l-context" @contextmenu="onContext">
    <slot />
    <Teleport to="body">
      <div
        v-if="open"
        ref="menuEl"
        class="l-ctx-menu"
        role="menu"
        :style="{ left: pos.x + 'px', top: pos.y + 'px' }"
        @keydown="onKey"
      >
        <template v-for="(it, i) in items" :key="i">
          <div v-if="it.separator" class="sep" role="separator" />
          <button
            v-else
            type="button"
            role="menuitem"
            tabindex="-1"
            class="item"
            :class="{ destructive: it.destructive }"
            :disabled="it.disabled"
            @click="select(it)"
          >
            <LIcon v-if="it.icon" :name="it.icon" :size="13" class="item-icon" />
            <span>{{ it.label }}</span>
          </button>
        </template>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.l-context {
  display: contents;
}
</style>

<style>
/* unscoped because the menu is teleported to <body>, outside this component's
   scoped-style range. Positioned at the pointer. */
.l-ctx-menu {
  position: fixed;
  min-width: 200px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  padding: 5px;
  z-index: 100;
}
.l-ctx-menu .item {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  width: 100%;
  text-align: left;
  font-size: var(--text-base);
  color: var(--text);
  padding: 8px 10px;
  border-radius: var(--r-sm);
  min-height: var(--target-min);
  background: none;
  border: 0;
  cursor: pointer;
}
.l-ctx-menu .item:hover:not(:disabled) {
  background: var(--surface-sunken);
}
.l-ctx-menu .item:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: -2px;
}
.l-ctx-menu .item:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.l-ctx-menu .item.destructive {
  color: var(--status-rejected-text);
}
.l-ctx-menu .item.destructive:hover:not(:disabled) {
  background: var(--status-rejected-fill);
}
.l-ctx-menu .item-icon {
  color: var(--icon);
  width: 15px;
  text-align: center;
}
.l-ctx-menu .sep {
  height: 1px;
  background: var(--line-soft);
  margin: 5px 0;
}
</style>
